import type { PublicationCode } from '@/scopes/common/domain/models'
import { space } from '@sunwebgroup/ui-components'
import { Accordion, Button, TextArea } from '@sunwebgroup/ui-components/uds'
import { ChangeEvent, FormEvent, KeyboardEvent, use, useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { useAiFilter } from '@/scopes/search/search/domain/hooks'
import { showAIFilter } from '@/scopes/search/search/domain/policies/publicationCodes'
import { isFlightOnlySelector } from '@/scopes/search/search/infrastructure/store/selectors'
import { useSearchStoreSelector } from '@/scopes/search/search/infrastructure/store/useSearchStore'
import { FilterGroupProps } from '../types'

const MAX_LENGTH = 250

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

export function IntelligentFilter({ defaultOpen }: FilterGroupProps) {
  const intl = useIntl()
  const { setUserQuery, isFetching, isAIError, userQuery } = useAiFilter()
  const { sendEvent } = useAnalytics()
  const isFlightOnly = useSearchStoreSelector(isFlightOnlySelector)
  const { isWinter, publicationCode } = use(CatalogueDetailsContext)

  const [value, setValue] = useState(userQuery ?? '')

  useEffect(() => {
    setValue(userQuery ?? '')
  }, [userQuery])

  const confirm = useCallback(() => {
    setUserQuery(value)
    sendEvent(
      createSearchFilterClickEvent({
        element: 'textField',
        type: 'AI question',
        result: value,
      }),
    )
  }, [value, setUserQuery, sendEvent])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      if (newValue.length <= MAX_LENGTH) {
        setValue(newValue)
      }
    },
    [setValue],
  )

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      confirm()
    },
    [confirm],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        confirm()
      }
    },
    [confirm],
  )

  const showAIFilterWG = useFeatureFlag('BAHAMAS_ENABLE_AI_FILTER_WG')
  const showFilter = showAIFilter(publicationCode as PublicationCode, showAIFilterWG) && !isFlightOnly

  return (
    showFilter && (
      <Accordion.Item title={intl.formatMessage({ id: 'search_ai_filter_label' })} defaultOpen={defaultOpen} data-qa="intelligent-filter">
        <Form onSubmit={handleSubmit}>
          <TextArea
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isFetching}
            placeholder={intl.formatMessage({ id: isWinter ? 'search_ai_filter_placeholder_winter' : 'search_ai_filter_placeholder' })}
            label={intl.formatMessage({ id: 'search_ai_filter_hint' })}
            status={isAIError ? 'error' : 'default'}
            feedbackText={intl.formatMessage({ id: 'search_ai_filter_error' })}
            data-qa="intelligent-filter-input"
          />

          <Button type="submit" disabled={isFetching} variant="secondary" size="S" fullWidth data-qa="intelligent-filter-submit">
            {intl.formatMessage({ id: 'search_ai_filter_submit_text' })}
          </Button>
        </Form>
      </Accordion.Item>
    )
  )
}
